<div class="w-full p-4 lg:p-6 space-y-6 bg-base-200 min-h-screen">
  <!-- Welcome Header -->
  <div class="mb-6">
    <h1 class="text-3xl font-bold text-primary mb-2">
      ¡Bienvenido {{ (user()?.name || user()?.username || 'Usuario').split(' ')[0] }}!
    </h1>
    @if (showDashboard) {
    <p class="text-base-content/70 text-lg">Aquí está el resumen de tu actividad de cotizaciones</p>
    } @else {
    <p class="text-base-content/70 text-lg">Descubre nuestras últimas novedades y promociones</p>
    }
  </div>

  @if (!showDashboard) {
    <app-banner-carousel></app-banner-carousel>
  } @else {
    <!-- Loading State -->
    @if (isLoading()) {
    <div class="flex items-center justify-center min-h-[400px]">
      <span class="loading loading-spinner loading-lg text-error"></span>
    </div>
    }

    <!-- Error State -->
    @else if (error()) {
    <div class="alert alert-error">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="stroke-current shrink-0 h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{{ error() }}</span>
      <button class="btn btn-sm btn-ghost" (click)="loadDashboardData()">Reintentar</button>
    </div>
    }

    <!-- Dashboard Content -->
    @else if (dashboardData(); as data) {
    <!-- KPIs Principales - Fila Superior (4 tarjetas principales) -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Total Cotizaciones -->
      <app-kpi-card
        [title]="'Total Cotizaciones'"
        [value]="data.kpis.totalQuotes"
        [changeIndicator]="'+12.5%'"
        [changeColor]="'success'"
        [secondaryText]="'vs mes anterior'"
        [showIcon]="true"
        [iconColor]="'primary'"
        [iconBgColor]="'primary'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-blue-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </app-kpi-card>

      <!-- Aprobadas -->
      <app-kpi-card
        [title]="'Aprobadas'"
        [value]="data.kpis.approvedQuotes"
        [changeIndicator]="'+8.3%'"
        [changeColor]="'success'"
        [secondaryText]="'vs mes anterior'"
        [showIcon]="true"
        [iconColor]="'success'"
        [iconBgColor]="'success'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </app-kpi-card>

      <!-- Pendientes -->
      <app-kpi-card
        [title]="'Pendientes'"
        [value]="data.kpis.pendingQuotes"
        [changeIndicator]="'-5.2%'"
        [changeColor]="'error'"
        [secondaryText]="'vs mes anterior'"
        [showIcon]="true"
        [iconColor]="'warning'"
        [iconBgColor]="'warning'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-orange-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </app-kpi-card>

      <!-- Total Primas -->
      <app-kpi-card
        [title]="'Total Primas'"
        [value]="formatCurrencyShort(data.kpis.totalPremiums)"
        [changeIndicator]="'+15.8%'"
        [changeColor]="'success'"
        [secondaryText]="'vs mes anterior'"
        [showIcon]="true"
        [iconColor]="'info'"
        [iconBgColor]="'info'"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6 text-purple-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </app-kpi-card>
    </div>

    <!-- Segunda Fila de KPIs (3 tarjetas adicionales) -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Tasa de Conversión -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <div class="flex-1 flex flex-col">
          <h3 class="text-sm font-medium text-base-content/70 mb-2">Tasa de Conversión</h3>
          <div class="flex items-baseline gap-2 mb-3">
            <p class="text-2xl font-bold text-base-content">
              {{ formatPercent(data.kpis.conversionRate) }}
            </p>
          </div>
          <div class="w-full bg-base-200 rounded-full h-2.5 mb-2">
            <div
              class="bg-success h-2.5 rounded-full transition-all duration-300"
              [style.width.%]="data.kpis.conversionRate"
            ></div>
          </div>
          <p class="text-xs text-base-content/50 mt-auto">
            {{ data.kpis.approvedQuotes }} de {{ data.kpis.totalQuotes }} cotizaciones aprobadas
          </p>
        </div>
      </app-card>

      <!-- Valor Promedio por Cotización -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <div class="flex-1 flex flex-col">
          <h3 class="text-sm font-medium text-base-content/70 mb-2">Valor Promedio por Cotización</h3>
          <p class="text-2xl font-bold text-base-content mb-2">
            {{ formatCurrency(data.kpis.averageValuePerQuote) }}
          </p>
          <p class="text-xs text-base-content/50 mt-auto">Basado en cotizaciones aprobadas</p>
        </div>
      </app-card>

      <!-- Rechazadas -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <div class="flex-1 flex flex-col">
          <h3 class="text-sm font-medium text-base-content/70 mb-2">Rechazadas</h3>
          <p class="text-2xl font-bold text-base-content mb-2">{{ data.kpis.rejectedQuotes }}</p>
          <p class="text-xs text-base-content/50 mt-auto">
            {{ formatPercent(data.kpis.rejectedPercentage) }} del total
          </p>
        </div>
      </app-card>
    </div>

    <!-- Gráficos - Fila Media -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Gráfico de Tendencia (Líneas) -->
      <app-card variant="shadow-bordered" bodyPadding="md">
        <h3 class="text-base font-semibold text-base-content mb-1">Tendencia de Cotizaciones</h3>
        <p class="text-xs text-base-content/50 mb-4">Últimos 6 meses</p>
        @if (data.trendData && data.trendData.length > 0) { @let maxValue =
        getMaxTrendValue(data.trendData);
        <div class="relative h-64">
          <!-- SVG del gráfico mejorado -->
          <svg class="w-full h-full" viewBox="0 0 600 200" preserveAspectRatio="xMidYMid meet">
            <!-- Grid lines horizontales -->
            @for (i of [0, 1, 2, 3, 4]; track i) {
            <line
              x1="40"
              [attr.y1]="20 + i * 40"
              x2="560"
              [attr.y2]="20 + i * 40"
              stroke="#e5e7eb"
              stroke-width="1"
              class="opacity-50"
            />
            }

            <!-- Eje Y - Etiquetas -->
            @for (i of [0, 1, 2, 3, 4]; track i) {
            <text
              x="35"
              [attr.y]="25 + i * 40"
              text-anchor="end"
              class="text-xs fill-base-content/60"
              font-size="10"
            >
              {{ Math.round(maxValue - (maxValue / 4) * i) }}
            </text>
            }

            <!-- Línea de Cotizaciones (azul) -->
            <path
              [attr.d]="getLinePath(data.trendData, maxValue, 'quotes')"
              fill="none"
              stroke="#3b82f6"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="transition-all duration-500"
            />

            <!-- Línea de Aprobadas (verde) -->
            <path
              [attr.d]="getLinePath(data.trendData, maxValue, 'approved')"
              fill="none"
              stroke="#10b981"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="transition-all duration-500"
            />

            <!-- Puntos para Cotizaciones -->
            @for (point of data.trendData; track point.month; let i = $index) {
            <circle
              [attr.cx]="getLineX(i, data.trendData.length)"
              [attr.cy]="getLineY(point.quotes, maxValue)"
              r="4"
              fill="#3b82f6"
              stroke="#ffffff"
              stroke-width="2"
              class="transition-all duration-300"
            />
            }

            <!-- Puntos para Aprobadas -->
            @for (point of data.trendData; track point.month; let i = $index) {
            <circle
              [attr.cx]="getLineX(i, data.trendData.length)"
              [attr.cy]="getLineY(point.approved, maxValue)"
              r="4"
              fill="#10b981"
              stroke="#ffffff"
              stroke-width="2"
              class="transition-all duration-300"
            />
            }

            <!-- Etiquetas de meses en la parte inferior -->
            @for (point of data.trendData; track point.month; let i = $index) {
            <text
              [attr.x]="getLineX(i, data.trendData.length)"
              y="190"
              text-anchor="middle"
              class="text-xs fill-base-content/70"
              font-size="11"
              font-weight="500"
            >
              {{ point.month }}
            </text>
            }
          </svg>
        </div>
        <!-- Leyenda -->
        <div class="flex gap-6 mt-4 text-xs">
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 bg-primary rounded-full"></div>
            <span class="text-base-content/70 font-medium">Cotizaciones</span>
          </div>
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 bg-success rounded-full"></div>
            <span class="text-base-content/70 font-medium">Aprobadas</span>
          </div>
        </div>
        } @else {
        <div class="h-52 flex items-center justify-center text-base-content/50">
          <p>No hay datos disponibles</p>
        </div>
        }
      </app-card>

      <!-- Distribución por Estado -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <div class="flex-1 flex flex-col">
          <h3 class="text-base font-semibold text-base-content mb-1">Distribución por Estado</h3>
          <p class="text-xs text-base-content/50 mb-4">Estado actual de cotizaciones</p>
          @if (data.statusDistribution && data.statusDistribution.length > 0) { @let pieData =
          getPieChartData(data.statusDistribution);
          <div class="flex items-center justify-center gap-8 flex-1 min-h-[280px]">
            <!-- Gráfico de pastel -->
            <div class="relative w-48 h-48 flex-shrink-0">
              <svg class="w-48 h-48 transform -rotate-90" viewBox="0 0 100 100">
                @for (segment of pieData; track segment.label; let i = $index) {
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  [attr.stroke]="segment.color"
                  stroke-width="20"
                  [attr.stroke-dasharray]="(segment.angle / 360) * 251.327 + ' 251.327'"
                  [attr.stroke-dashoffset]="-(segment.offset / 360) * 251.327"
                  class="transition-all duration-300"
                ></circle>
                }
              </svg>
            </div>
            <!-- Leyenda -->
            <div class="flex-1 space-y-4">
              @for (item of data.statusDistribution; track item.label) {
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div
                    class="w-5 h-5 rounded-full"
                    [class.bg-success]="item.color === 'success'"
                    [class.bg-warning]="item.color === 'warning'"
                    [class.bg-error]="item.color === 'error'"
                  ></div>
                  <span class="text-sm font-medium text-base-content">{{ item.label }}</span>
                </div>
                <span class="text-sm font-bold text-base-content">{{
                  formatPercent(item.percentage)
                }}</span>
              </div>
              }
            </div>
          </div>
          } @else {
          <div class="h-64 flex items-center justify-center text-base-content/50 flex-1">
            <p>No hay datos disponibles</p>
          </div>
          }
        </div>
      </app-card>
    </div>

    <!-- Gráficos y Listas - Fila Inferior -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <!-- Por Tipo de Vehículo -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <h3 class="text-base font-semibold text-base-content mb-1">Por Tipo de Vehículo</h3>
        <p class="text-xs text-base-content/50 mb-4">Distribución de cotizaciones</p>
        @if (data.vehicleTypeDistribution && data.vehicleTypeDistribution.length > 0) { @let maxCount
        = getMaxVehicleTypeCount(data.vehicleTypeDistribution);
        <div class="space-y-4 flex-1">
          @for (item of data.vehicleTypeDistribution; track item.type) {
          <div>
            <div class="flex justify-between items-center mb-2">
              <span class="text-sm font-medium text-base-content">{{ item.type }}</span>
              <span class="text-sm font-bold text-base-content">{{ item.count }}</span>
            </div>
            <div class="w-full bg-base-200 rounded-full h-3">
              <div
                class="bg-primary h-3 rounded-full transition-all duration-300"
                [style.width.%]="maxCount > 0 ? (item.count / maxCount) * 100 : 0"
              ></div>
            </div>
          </div>
          }
        </div>
        } @else {
        <div class="h-32 flex items-center justify-center text-base-content/50 flex-1">
          <p>No hay datos disponibles</p>
        </div>
        }
      </app-card>

      <!-- Top Productores -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <h3 class="text-base font-semibold text-base-content mb-1">Top Productores</h3>
        <p class="text-xs text-base-content/50 mb-4">Mejores desempeños del mes</p>
        @if (data.topProducers && data.topProducers.length > 0) {
        <div class="space-y-3 flex-1">
          @for (producer of data.topProducers; track producer.producerName; let i = $index) {
          <div
            class="flex items-center justify-between p-3 bg-base-200 rounded-lg hover:bg-base-300 transition-colors"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm flex-shrink-0"
              >
                {{ i + 1 }}
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-semibold text-base-content truncate">
                  {{ producer.producerName }}
                </p>
                <p class="text-xs text-base-content/50">
                  {{ producer.approvedQuotes }} aprobadas de {{ producer.totalQuotes }}
                </p>
              </div>
            </div>
            <div class="text-right flex-shrink-0 ml-2">
              <p class="text-sm font-bold text-base-content">
                {{ formatCurrencyShort(producer.totalPremiums) }}
              </p>
            </div>
          </div>
          }
        </div>
        } @else {
        <div class="h-32 flex items-center justify-center text-base-content/50 flex-1">
          <p>No hay datos disponibles</p>
        </div>
        }
      </app-card>

      <!-- Cotizaciones Recientes -->
      <app-card variant="shadow-bordered" bodyPadding="md" additionalClasses="h-full flex flex-col">
        <h3 class="text-base font-semibold text-base-content mb-1">Cotizaciones Recientes</h3>
        <p class="text-xs text-base-content/50 mb-4">Últimas actividades en el sistema</p>
        @if (data.recentQuotes && data.recentQuotes.length > 0) {
        <div class="space-y-3 flex-1">
          @for (quote of data.recentQuotes; track quote.quoteNumber) {
          <a
            [routerLink]="['/quotes', quote.quoteNumber]"
            class="flex items-center gap-3 p-3 bg-base-200 rounded-lg hover:bg-base-300 transition-colors cursor-pointer"
          >
            <!-- Icono -->
            <div
              class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>

            <!-- Información de la cotización -->
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-primary mb-1">
                {{ formatQuoteNumber(quote.quoteNumber) }}
              </p>
              <p class="text-xs text-base-content/70 truncate">
                {{ quote.clientName }} · {{ quote.vehicleInfo }}
              </p>
            </div>

            <!-- Monto y fecha -->
            <div class="text-right flex-shrink-0 ml-2">
              <p class="text-sm font-bold text-base-content mb-1">
                {{ formatCurrency(quote.premium) }}
              </p>
              <p class="text-xs text-base-content/50">{{ formatDateRelative(quote.date) }}</p>
            </div>

            <!-- Badge de estado -->
            <div class="flex-shrink-0 ml-2">
              <span
                [class]="'badge badge-sm font-medium ' + getQuoteStatusBadgeClass(quote.statusCode)"
              >
                {{ quote.status }}
              </span>
            </div>
          </a>
          }
        </div>
        } @else {
        <div class="h-32 flex items-center justify-center text-base-content/50 flex-1">
          <p>No hay datos disponibles</p>
        </div>
        }
      </app-card>
    </div>
    }
  }
</div>
